import axios from "axios";
import { ORG_AUTH_REFRESH } from "../components/ApiConst";
import { HTTP_CODE } from "./HttpCode";
import { errorToastMsg } from "./Utils";

// These belong to the browser, not to the login: wiping device_id made every
// next login look like a new device, and fcm_token is this browser's push
// registration.
const BROWSER_KEYS = ["device_id", "fcm_token"];

export const clearLoginStorage = () => {
  const kept = BROWSER_KEYS.map((key) => [key, localStorage.getItem(key)] as const);
  localStorage.clear();
  kept.forEach(([key, value]) => {
    if (value !== null) localStorage.setItem(key, value);
  });
};

// The session is gone (logged in on another device / logged out): back to start.
export const endLoginSession = () => {
  if (localStorage.getItem("token")) {
    clearLoginStorage();
    window.location.href = "/";
  }
};

let refreshing: Promise<boolean> | null = null;

// Gets a new access token with the stored refresh token. Parallel 401s share
// one call, and the refresh token is read when the call is made - not when the
// module loaded, which went stale after the first refresh.
export const refreshAccessToken = (): Promise<boolean> => {
  if (refreshing) return refreshing;

  const refreshToken = localStorage.getItem("ref_token");
  if (refreshToken === null) return Promise.resolve(false);

  const refTokenHeader: any = {
    headers: {
      Authorization: "Basic " + localStorage.getItem("x-token"),
      "post-id": localStorage.getItem("post_id") as string,
      "session-id": localStorage.getItem("session_id") as string,
      "service-instance-id": localStorage.getItem("service-instance-id") as string,
      region: "uk",
      organization: "eln",
    },
  };
  refreshing = axios
    .create(refTokenHeader)
    .post(ORG_AUTH_REFRESH, { refresh_token: refreshToken })
    .then((resp) => {
      if (resp.status == HTTP_CODE.OK) {
        localStorage.setItem("token", resp?.data?.data?.access_token);
        localStorage.setItem("ref_token", resp?.data?.data?.refresh_token);
        return true;
      }
      return false;
    })
    .catch((e) => {
      errorToastMsg(e.message);
      return false;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
};

// Response-interceptor body shared by the axios instances: on a 401 refresh
// the token and replay the request once; if the refresh fails the session
// really ended, so log out. Returns null when the error isn't handled here.
export const retryAfterRefresh = async (error: any, instance: any) => {
  const request = error?.config;
  if (error?.response?.status !== 401 || !request || request._retriedAfterRefresh) return null;
  if (!localStorage.getItem("token")) return null;

  request._retriedAfterRefresh = true;
  if (await refreshAccessToken()) {
    return instance(request);
  }
  endLoginSession();
  return Promise.reject(error);
};
